import { ROUTES, PageTypes, PageStatus } from "@/App";
import AssignCareerSite from "@/pages/Branding/modals/AssignCareerSite";
import AssignJobAd from "@/pages/Branding/modals/AssignJobAd";
import {
  CustomerDomain,
  customersApi,
  useDeleteJobListPageFromDomainMutation,
  useDeleteCustomerDomainMutation,
  useDeleteJobAdPageFromDomainMutation,
  useAddJobAdPageToDomainMutation,
  useUpdateDomainLanguageMutation,
  useUpdateDomainEmbeddedInIframeMutation,
} from "@/redux/reducer/customers.api";
import {
  pagesApi,
  Template,
  useApplyFrameHeadMutation,
  useCloneTemplateMutation,
  CloneTemplatePayload,
} from "@/redux/reducer/pages.api";
import type { AppDispatch } from "@/redux/store";
import {
  applyFrameSetting,
  FramePageResult,
} from "@/pages/Editor/utils/frameSetting";
import { getUserSupportStatus, getUserTenantId } from "@talentech/bff-login";
import {
  ButtonColors,
  Alert,
  TalentechColors,
  Badge,
  Button,
  BootstrapSizes,
  Select,
  Switch,
} from "@talentech/components";
import clsx from "clsx";
import { useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import CloneTemplateModal from "../CloneTemplateModal/CloneTemplateModal";
import EditDomainAddressModal from "./EditDomainAddressModal";
import { components } from "react-select";
import { getLanguageDetails, languageOptions } from "@/shared/i18n_utils";

interface ErrorResponse {
  data: {
    detail: string;
  };
}

const DomainCard: React.FC<{
  domain: CustomerDomain;
  statusConfig: {
    label: string;
    color: string;
  };
  selectablePagesAndAds: {
    ads: Template[];
    pages: Template[];
  };
  pages: Template[];
  ads: Template[];
}> = ({ domain, statusConfig, pages, ads, ...props }) => {
  const [deleteJobListPage] = useDeleteJobListPageFromDomainMutation();
  const tenantId = useSelector(getUserTenantId);

  const [deleteModal, setDeleteModal] = useState(false);
  const onDeleteClick = () => {
    setDeleteModal(true);
  };

  const removeJobListPage = () => {
    toast
      .promise(
        deleteJobListPage({
          domain_id: domain.domain_id,
          joblist_page_id: domain?.joblist_page?.joblist_page_id,
          tenant_id: tenantId,
        }).unwrap(),
        {
          pending: "Deleting job list page...",
          success: "Job list page deleted successfully",
          error: {
            render({ data }: { data: ErrorResponse }) {
              return <>{data.data.detail}</>;
            },
          },
        }
      )
      .then(() => {
        setDeleteModal(false);
      });
  };

  const getPageDetails = useMemo(() => {
    const pageMap = new Map();
    pages.forEach((page) => pageMap.set(page.id, page));
    return (pageId: string) => pageMap.get(pageId);
  }, [pages]);

  const getAdDetails = useMemo(() => {
    const adMap = new Map();
    ads.forEach((ad) => adMap.set(ad.id, ad));
    return (adId: string) => adMap.get(adId);
  }, [ads]);

  const isSupport = useSelector(getUserSupportStatus);
  const [editAddressModal, setEditAddressModal] = useState(false);

  const [deleteDomainModal, setDeleteDomainModal] = useState(false);
  const [deleteDomain] = useDeleteCustomerDomainMutation();
  const removeDomain = () => {
    toast
      .promise(
        deleteDomain({
          tenant_id: tenantId,
          domain_id: domain.domain_id,
        }).unwrap(),
        {
          pending: "Deleting domain...",
          success: "Domain deleted successfully",
          error: {
            render({ data }: { data: ErrorResponse }) {
              return <>{data.data.detail}</>;
            },
          },
        }
      )
      .then(() => {
        setDeleteDomainModal(false);
      });
  };

  const [assignCareerSiteModal, setAssignCareerSiteModal] = useState(false);
  const [assignJobAd, setAssignJobAd] = useState(false);

  const [deleteJobAdModal, setDeleteJobAdModal] = useState("");

  const [deleteJobAdPage] = useDeleteJobAdPageFromDomainMutation();
  const [addJobAdToDomain] = useAddJobAdPageToDomainMutation();
  const removeJobAdPage = () => {
    deleteJobAdModal &&
      toast
        .promise(
          deleteJobAdPage({
            job_ad_page_id: deleteJobAdModal,
            tenant_id: tenantId,
            domain_id: domain.domain_id,
            joblist_page_id: domain.joblist_page.joblist_page_id,
          }).unwrap(),
          {
            pending: "Deleting job ad page...",
            success: "Job ad page deleted successfully",
            error: {
              render({ data }: { data: ErrorResponse }) {
                return <>{data.data.detail}</>;
              },
            },
          }
        )
        .then(() => {
          setDeleteJobAdModal("");
        });
  };

  const [cloneTemplateMutation, { isLoading: isCloneLoading }] =
    useCloneTemplateMutation();
  const [cloneTemplateModal, setCloneTemplateModal] = useState<
    (CloneTemplatePayload & { newName: string }) | null
  >(null);

  const onCloneActionClick = (adId: string) => {
    const adToClone = ads.find((ad) => ad.id === adId);
    if (adToClone) {
      const defaultNewName = `Copy of ${adToClone.name}`;
      setCloneTemplateModal({
        id: adToClone.id,
        type: PageTypes.JOB,
        status: adToClone.published ? PageStatus.PUBLISHED : PageStatus.DRAFT,
        newName: defaultNewName,
      } as CloneTemplatePayload & { newName: string });
    }
  };

  const onCloneActionConfirmationClick = () => {
    if (cloneTemplateModal) {
      const { newName, ...cloneParams } = cloneTemplateModal;
      toast
        .promise(
          cloneTemplateMutation({
            ...cloneParams,
            new_name: newName,
          }).unwrap(),
          {
            pending: "Cloning...",
            success: "Template cloned successfully",
            error: "Error cloning template",
          }
        )
        .then((cloneResult) => {
          setCloneTemplateModal(null);
          if (
            cloneResult &&
            cloneResult.id &&
            domain?.joblist_page?.joblist_page_id
          ) {
            toast.promise(
              addJobAdToDomain({
                domain_id: domain.domain_id,
                joblist_page_id: domain.joblist_page.joblist_page_id,
                tenant_id: tenantId,
                job_ad_page_id: cloneResult.id,
              }).unwrap(),
              {
                pending: "Associating cloned job ad with domain...",
                success: "Cloned job ad associated with domain!",
                error: "Failed to associate cloned job ad with domain",
              }
            ).then(onPageAttached);
          }
        });
    }
  };

  const [updateDomainLanguage] = useUpdateDomainLanguageMutation();
  const onLanguageChange = (language: { value: string }) => {
    console.log(`change language to: ${language.value}`);
    toast.promise(
      updateDomainLanguage({
        domain_id: domain.domain_id,
        language: language.value,
        tenant_id: tenantId,
      }).unwrap(),
      {
        pending: "Updating domain language...",
        success: "Domain language updated successfully",
        error: "Failed to update domain language",
      }
    );
  };

  const [updateDomainEmbeddedInIframe] =
    useUpdateDomainEmbeddedInIframeMutation();
  const dispatch = useDispatch<AppDispatch>();
  const [applyFrameHead] = useApplyFrameHeadMutation();
  const [framePagesToFix, setFramePagesToFix] = useState<FramePageResult[]>([]);

  // Brings the domain's pages to the iframe setting their domains call for; `embedded` is this domain's new setting.
  // Config and templates are read again: an assign or a clone has just changed them.
  const applyDomainFrameSetting = async (embedded = !!domain.embedded_in_iframe) => {
    const configRequest = dispatch(
      customersApi.endpoints.getCustomerConfig.initiate(
        { tenantId },
        { forceRefetch: true }
      )
    );
    const templatesRequest = dispatch(
      pagesApi.endpoints.getPages.initiate({}, { forceRefetch: true })
    );
    let config, templates;
    try {
      [config, templates] = await Promise.all([
        configRequest.unwrap(),
        templatesRequest.unwrap(),
      ]);
    } finally {
      configRequest.unsubscribe();
      templatesRequest.unsubscribe();
    }
    const results = await applyFrameSetting({
      domainId: domain.domain_id,
      domains: config.domains.map((d) =>
        d.domain_id === domain.domain_id ? { ...d, embedded_in_iframe: embedded } : d
      ),
      templates: { pages: templates.page_templates, ads: templates.job_templates },
      settings: config.settings,
      tenantId,
      applyHead: (page) => applyFrameHead(page).unwrap(),
    });
    setFramePagesToFix(results.filter((page) => page.status === "skipped"));
  };

  const onEmbeddedInIframeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const embedded = event.target.checked;
    // Pages first: a run that fails halfway leaves the switch as it was, so switching again finishes it.
    const update = applyDomainFrameSetting(embedded).then(() =>
      updateDomainEmbeddedInIframe({
        domain_id: domain.domain_id,
        embedded_in_iframe: embedded,
        tenant_id: tenantId,
      }).unwrap()
    );
    toast.promise(update, {
      pending: "Updating the domain and its pages...",
      success: "Domain iframe setting updated, and its pages with it",
      error: "Failed to update the domain iframe setting",
    });
  };

  const onPageAttached = () => {
    if (!domain.embedded_in_iframe) {
      return;
    }
    toast.promise(applyDomainFrameSetting(), {
      pending: "Updating the page for the iframe setting...",
      success: "Page updated for the iframe setting",
      error: "Failed to update the page for the iframe setting, save and publish it in the editor",
    });
  };

  const copyDomainId = () => {
    navigator.clipboard.writeText(domain.domain_id);
    toast.success("Domain ID copied to clipboard");
  };

  return (
    <>
      <ConfirmationModal
        show={deleteModal}
        onClose={() => {
          setDeleteModal(false);
        }}
        onConfirm={removeJobListPage}
        title="Remove job list page"
        confirmButtonText="Remove"
        variant={ButtonColors.DANGER}
      >
        <Alert
          color={TalentechColors.DANGER}
          message="Are you certain you wish to remove the assignment of this career site from the selected domain?"
        />
      </ConfirmationModal>

      <div className="col">
        <div className="card h-100">
          <div className="card-body">
            <div className="d-flex justify-content-between border-bottom mb-3">
              <div className="col">
                <h2 className="card-title">{domain.domain_name}</h2>
              </div>
              <div className="col text-end">
                <Badge color={statusConfig.color}>{statusConfig.label}</Badge>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-center">
                <a
                  href={domain.domain_url}
                  target="_blank"
                  className="text-break"
                >
                  {domain.domain_url}
                  <span className="fa-light fa-sharp fa-arrow-up-right-from-square ps-1 fa-sm">
                    <span className="visually-hidden">Open link in new tab</span>
                  </span>
                </a>
              <div className="d-flex gap-1">
                {domain.domain_type === "hosted" && isSupport && (
                  <Button
                    color={ButtonColors.LIGHT}
                    className="rounded-1"
                    icon={<span className="fa-sharp fa-light fa-pen" aria-label="Edit address" />}
                    onClick={() => setEditAddressModal(true)}
                  />
                )}
                <Button
                  color={ButtonColors.LIGHT}
                  className="rounded-1"
                  icon={
                    <span
                      className="fa-sharp fa-light fa-trash text-danger"
                      aria-label="Delete template"
                    />
                  }
                  onClick={() => setDeleteDomainModal(true)}
                />
              </div>
            </div>
            {editAddressModal && (
              <EditDomainAddressModal domain={domain} onClose={() => setEditAddressModal(false)} />
            )}


            <h3 className="h5 mt-4">Career site</h3>
            <div className="row">
              <ul className="list-group list-group-flush">
                <li
                  className={clsx(
                    "list-group-item d-flex justify-content-between align-items-start px-0",
                    {
                      "border-0": !domain?.joblist_page?.joblist_page_id,
                    }
                  )}
                >
                  <div
                    className={clsx(
                      domain?.joblist_page?.joblist_page_id
                        ? "ms-2 me-auto"
                        : "w-100"
                    )}
                  >
                    {domain?.joblist_page?.joblist_page_id ? (
                      <div className="fw-bold">
                        <i className="fa-light fa-sharp fa-briefcase fa-lg pe-2"></i>
                        {
                          getPageDetails(domain.joblist_page?.joblist_page_id)
                            ?.name
                        }
                      </div>
                    ) : (
                      <Button
                        outline
                        block
                        color={ButtonColors.PRIMARY}
                        label="Assign career site"
                        icon={<i className="fa-sharp fa-briefcase me-2" />}
                        onClick={() => {
                          setAssignCareerSiteModal(true);
                        }}
                      />
                    )}
                  </div>
                  {domain?.joblist_page?.joblist_page_id && (
                    <div className="col justify-content-end d-flex">
                      <div className="btn-group" role="group">
                        <a
                          href={domain.domain_url}
                          target="_blank"
                          className="btn btn-light rounded-1"
                        >
                          <span
                            className="fa-sharp fa-light fa-eye"
                            aria-label="View template"
                          />
                        </a>

                        <Link
                          to={ROUTES.EDITOR.replace(
                            ":type",
                            PageTypes.PAGE
                          ).replace(
                            ":id",
                            domain.joblist_page?.joblist_page_id
                          )}
                          className="btn btn-light rounded-1"
                          viewTransition={true}
                        >
                          <span
                            className="fa-sharp fa-light fa-pencil"
                            aria-label="Edit template"
                          />
                        </Link>
                        <Button
                          color={ButtonColors.LIGHT}
                          className="rounded-1"
                          onClick={onDeleteClick}
                          icon={
                            <span
                              className="fa-sharp fa-light fa-trash text-danger"
                              aria-label="Delete template"
                            />
                          }
                        />
                      </div>
                    </div>
                  )}
                </li>
              </ul>
            </div>

            {domain?.joblist_page?.joblist_page_id && (
              <>
                <h3 className="h5 mt-4">Job ads</h3>
                <div className="row pb-3">
                  {domain?.joblist_page?.job_ad_pages.length > 0 && (
                    <ul className="list-group list-group-flush">
                      {domain?.joblist_page?.job_ad_pages.map((ad) => {
                        const adDetails = getAdDetails(ad.job_ad_page_id);
                        if (!adDetails)
                          return (
                            <Button
                              label="clear"
                              color={ButtonColors.DANGER}
                              outline
                              onClick={() => {
                                setDeleteJobAdModal(ad.job_ad_page_id);
                              }}
                              size={BootstrapSizes.SMALL}
                              className="rounded-1"
                              icon={
                                <i className="fa-sharp fa-light fa-trash me-2" />
                              }
                            />
                          );
                        return (
                          <li
                            className="list-group-item d-flex justify-content-between align-items-start px-0"
                            key={ad.job_ad_page_id}
                          >
                            <div className="ms-2 me-auto">
                              <div className="fw-bold">
                                <i
                                  className={clsx(
                                    "fa-light fa-lg fa-sharp  pe-2",
                                    {
                                      "fa-circle-pause text-danger":
                                        !adDetails.published,
                                      "fa-circle-check text-success":
                                        adDetails.published,
                                    }
                                  )}
                                ></i>
                                {adDetails.name}
                              </div>
                              {adDetails.published ? "Published" : "Draft"}
                            </div>
                            <div className="col justify-content-end d-flex">
                              <div className="btn-group" role="group">
                                <Link
                                  to={ROUTES.EDITOR.replace(
                                    ":type",
                                    PageTypes.JOB
                                  ).replace(":id", adDetails.id)}
                                  className="btn btn-light rounded-1"
                                  viewTransition={true}
                                >
                                  <span
                                    className="fa-sharp fa-light fa-pencil"
                                    aria-label="Edit template"
                                  />
                                </Link>

                                <Button
                                  color={ButtonColors.LIGHT}
                                  className="rounded-1"
                                  icon={
                                    <span
                                      className="fa-sharp fa-light fa-copy"
                                      aria-label="Clone template"
                                    />
                                  }
                                  onClick={() =>
                                    onCloneActionClick(adDetails.id)
                                  }
                                />

                                <Button
                                  color={ButtonColors.LIGHT}
                                  className="rounded-1"
                                  icon={
                                    <span
                                      className="fa-sharp fa-light fa-trash text-danger"
                                      aria-label="Delete template"
                                    />
                                  }
                                  onClick={() =>
                                    setDeleteJobAdModal(adDetails.id)
                                  }
                                />
                              </div>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="card-footer border-0">
            <div className="d-flex justify-content-between align-items-center">
              <Select
                onChange={(x) => onLanguageChange(x as { value: string })}
                options={languageOptions}
                components={{
                  Option: CustomOption,
                  SingleValue: CutomSingleValue,
                }}
                defaultValue={languageOptions.find(
                  (l) => l.value === domain.language
                )}
              />
              <Button
                color={ButtonColors.PRIMARY}
                disabled={!domain?.joblist_page?.joblist_page_id}
                label="Create job ad"
                onClick={() => setAssignJobAd(true)}
                icon={<i className="fa-sharp fa-plus me-1"></i>}
              />
            </div>

            <div className="mt-3">
              <Switch
                id={`embedded-in-iframe-${domain.domain_id}`}
                label="Embedded in an iframe"
                checked={!!domain.embedded_in_iframe}
                onChange={onEmbeddedInIframeChange}
                aria-describedby={`embedded-in-iframe-help-${domain.domain_id}`}
              />
              <small
                id={`embedded-in-iframe-help-${domain.domain_id}`}
                className="text-muted d-block"
              >
                Pages of this domain load no Cookiebot or Ontame while shown inside an iframe.
                Switching it updates the domain's pages at once.
              </small>
              {framePagesToFix.length > 0 && (
                <div className="mt-2">
                  <Alert
                    color={TalentechColors.WARNING}
                    message={`Save and publish these pages in the editor, the iframe setting could not be applied to them: ${framePagesToFix
                      .map((page) => page.name)
                      .join(", ")}`}
                  />
                </div>
              )}
            </div>


            <div className="d-flex justify-content-between border-top pt-3 mt-3 align-items-center">
              <small className="text-muted mt-1 ">
                Domain ID: <span className="fw-bold"> {domain.domain_id}</span></small>


              <Button
                color={ButtonColors.LIGHT}
                className="rounded-1"
                icon={
                  <span
                    className="fa-sharp fa-light fa-copy"
                    aria-label="Copy domain ID"
                  />
                }
                onClick={() =>
                  copyDomainId()
                }
              />
            </div>
          </div>
        </div>
      </div>

      <AssignCareerSite
        showModal={assignCareerSiteModal}
        toggleModal={() => setAssignCareerSiteModal(!assignCareerSiteModal)}
        options={props.selectablePagesAndAds.pages.map((page) => ({
          value: page.id,
          label: page.name,
        }))}
        tenant_id={tenantId}
        domain_id={domain.domain_id}
        onAssigned={onPageAttached}
      />

      <AssignJobAd
        showModal={assignJobAd}
        toggleModal={() => setAssignJobAd(!assignJobAd)}
        options={props.selectablePagesAndAds.ads.map((ad) => ({
          value: ad.id,
          label: ad.name,
        }))}
        tenant_id={tenantId}
        domain_id={domain.domain_id}
        joblist_page_id={domain?.joblist_page?.joblist_page_id}
        onAssigned={onPageAttached}
      />

      <ConfirmationModal
        show={!!deleteDomainModal}
        onClose={() => {
          setDeleteDomainModal(false);
        }}
        onConfirm={() => {
          removeDomain();
        }}
        title="Delete domain"
        confirmButtonText="Delete"
        variant={ButtonColors.DANGER}
      >
        <Alert
          color={TalentechColors.DANGER}
          message="Are you certain you wish to delete this domain?"
        />
      </ConfirmationModal>

      <ConfirmationModal
        show={!!deleteJobAdModal}
        onClose={() => {
          setDeleteJobAdModal("");
        }}
        onConfirm={removeJobAdPage}
        title="Remove job ad page"
        confirmButtonText="Remove"
        variant={ButtonColors.DANGER}
      >
        <Alert
          color={TalentechColors.DANGER}
          message="Are you certain you wish to remove the assignment of this job advertisement from the selected domain?"
        />
      </ConfirmationModal>

      <CloneTemplateModal
        showModal={!!cloneTemplateModal}
        onClose={() => setCloneTemplateModal(null)}
        onCloneConfirm={onCloneActionConfirmationClick}
        isLoading={isCloneLoading}
        template={ads.find((ad) => ad.id === cloneTemplateModal?.id)}
        cloneData={cloneTemplateModal}
        setCloneData={setCloneTemplateModal}
      />
    </>
  );
};

export default DomainCard;

const CustomOption = ({ children, ...props }: any) => {
  const { svg, ...lng } = getLanguageDetails(props.data.value);
  return (
    <components.Option {...props} className="d-flex align-items-center">
      <img
        src={svg}
        alt={props.data.value}
        style={{
          width: "16px",
          height: "16px",
        }}
      />
      <div className="ms-2 text-nowrap">{lng.officialLanguageNameLocal}</div>
    </components.Option>
  );
};

const CutomSingleValue = ({ children, ...props }: any) => {
  const { svg, ...lng } = getLanguageDetails(props.data.value);

  return (
    <components.SingleValue {...props} className="d-flex align-items-center">
      <img
        src={svg}
        alt={props.data.value}
        style={{
          width: "16px",
          height: "16px",
        }}
      />
      <div className="ms-2 text-nowrap">{lng.officialLanguageNameLocal}</div>
    </components.SingleValue>
  );
};
